import type {Viewer} from '../viewer/Viewer';
import type * as THREE from 'three/webgpu';
import {resolveComponentInstanceTransformTarget} from '../assembly/component-instance';
import {
  LinearComponentPatternDirectionMode,
  LINEAR_COMPONENT_PATTERN_DIRECTION_REFERENCE_SLOTS,
  LinearComponentPatternLifecycle,
  LinearComponentPatternReferenceDescriptorKind,
  LinearComponentPatternReferenceOffsetMode,
  LinearComponentPatternRotationReferencePoint,
  LinearComponentPatternSelectionSlot,
  LinearComponentPatternTopologyProxyKind,
} from './LinearComponentPatternTypes';
import type {
  LinearComponentPatternCommitData,
  LinearComponentPatternDefinition,
  LinearComponentPatternDirectionDefinitionState,
  LinearComponentPatternReferenceDescriptor,
  LinearComponentPatternSelectionChangeListener,
  LinearComponentPatternSelectionChangePayload,
  LinearComponentPatternState,
  LinearComponentPatternViewModel,
} from './LinearComponentPatternTypes';
import {LinearComponentPatternPreviewRenderer} from './LinearComponentPatternPreviewRenderer';
import {ImportedModelObjectMetadataService} from '../imported-model/metadata';
import {FeatureResultMesh, isFeatureFace, isFeatureLine, isFeaturePoint} from '../mesh/feature';
import {resolveTopologySelectionPick} from '../utils/feature/resolveTopologySelectionPick';
import {
  resolveImportedAssemblyTopologySelectionRef
} from '../imported-model/selection/resolveImportedAssemblyTopologySelectionRef';
import {resolveAssemblyTopologyReferenceObject} from '../reference/shared/resolveAssemblyTopologyReferenceObject';
import {isImportedModelTopologyHost} from '../imported-model/topology/imported-model-topology-runtime';
import {convertMmToM} from '../utils/unit';
import {LinearComponentPatternComponentDisplayNameResolver} from './LinearComponentPatternComponentDisplayNameResolver';
import {MoveAlongEntityReferenceResolver} from '../assembly/move-component/move';
import {LinearComponentPatternDirectionReferenceResolver} from './LinearComponentPatternDirectionReferenceResolver';

/**
 * 方向 1 默认间距（米，对应 UI 50mm）。
 */
const DEFAULT_DIRECTION1_SPACING = convertMmToM(50);

/**
 * 方向 1 默认实例数（含源实例）。
 */
const DEFAULT_DIRECTION1_INSTANCES = 3;

/**
 * 方向 2 默认间距（米，对应 UI 50mm）。
 */
const DEFAULT_DIRECTION2_SPACING = convertMmToM(50);

/**
 * 方向 2 默认实例数（含源实例）。
 */
const DEFAULT_DIRECTION2_INSTANCES = 1;

/**
 * 默认到参考偏移距离（米）。
 */
const DEFAULT_REFERENCE_OFFSET_DISTANCE = 0;

/**
 * 默认到参考驱动模式。
 */
const DEFAULT_REFERENCE_OFFSET_MODE = LinearComponentPatternReferenceOffsetMode.BoundingBoxCenter;

/**
 * 方向 1 默认旋转实例角度增量（角度制）。
 */
const DEFAULT_DIRECTION1_ROTATION_ANGLE = 0;

/**
 * 创建默认线性零部件阵列状态。
 *
 * @returns 默认状态。
 */
function createDefaultState(): LinearComponentPatternState {
  return {
    lifecycle: LinearComponentPatternLifecycle.Closed,
    activeSelectionSlot: LinearComponentPatternSelectionSlot.Direction1Reference,
    direction1ReferenceObjectId: null,
    direction1ReferenceDisplayName: '',
    direction1ReferenceDescriptor: null,
    direction1RotationAxisObjectId: null,
    direction1RotationAxisDisplayName: '',
    direction1RotationAxisDescriptor: null,
    direction1RotateInstances: false,
    direction1RotationAngle: DEFAULT_DIRECTION1_ROTATION_ANGLE,
    direction1ReverseRotation: false,
    direction1FixedRotationAxis: true,
    direction1AlignToSeed: false,
    direction1RotationReferencePoint: LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
    direction1ReferenceGeometryObjectId: null,
    direction1ReferenceGeometryDisplayName: '',
    direction1ReferenceGeometryDescriptor: null,
    direction1SelectedReferenceObjectId: null,
    direction1SelectedReferenceDisplayName: '',
    direction1SelectedReferenceDescriptor: null,
    direction1Mode: LinearComponentPatternDirectionMode.SpacingAndInstances,
    direction1Spacing: DEFAULT_DIRECTION1_SPACING,
    direction1Instances: DEFAULT_DIRECTION1_INSTANCES,
    direction1OffsetDistance: DEFAULT_REFERENCE_OFFSET_DISTANCE,
    direction1ReferenceOffsetMode: DEFAULT_REFERENCE_OFFSET_MODE,
    direction1SetSpacing: true,
    direction1SetInstances: false,

    direction1ReverseDirection: false,

    direction2ReferenceObjectId: null,
    direction2ReferenceDisplayName: '',
    direction2ReferenceDescriptor: null,
    direction2ReferenceGeometryObjectId: null,
    direction2ReferenceGeometryDisplayName: '',
    direction2ReferenceGeometryDescriptor: null,
    direction2SelectedReferenceObjectId: null,
    direction2SelectedReferenceDisplayName: '',
    direction2SelectedReferenceDescriptor: null,
    direction2Mode: LinearComponentPatternDirectionMode.SpacingAndInstances,
    direction2Spacing: DEFAULT_DIRECTION2_SPACING,
    direction2Instances: DEFAULT_DIRECTION2_INSTANCES,
    direction2OffsetDistance: DEFAULT_REFERENCE_OFFSET_DISTANCE,
    direction2ReferenceOffsetMode: DEFAULT_REFERENCE_OFFSET_MODE,
    direction2SetSpacing: true,
    direction2SetInstances: false,

    direction2ReverseDirection: false,

    direction2PatternSeedOnly: false,
    componentObjectIds: [],
    skippedInstanceKeys: [],
    modifiedInstanceObjectIds: [],
  };
}

/**
 * 线性零部件阵列控制器。
 *
 * 设计说明：
 * 1. 本控制器只保存命令状态和选择槽位，不在当前阶段执行几何求解；
 * 2. 选择处理器通过本控制器读取当前槽位并写入选择结果；
 * 3. 后续预览、提交和持久化应继续在 pattern 模块内扩展，web-client 不直接实现阵列逻辑。
 */
export class LinearComponentPatternController {
  /** Viewer 实例。 */
  private readonly viewer: Viewer;
  /** 线性阵列预览渲染器。 */
  private readonly previewRenderer: LinearComponentPatternPreviewRenderer;
  /** 组件选择、提交和 UI 共同使用的 occurrence 显示名解析器。 */
  private readonly componentDisplayNameResolver: LinearComponentPatternComponentDisplayNameResolver;
  /** 与预览共用方向几何契约，阻止无效引用污染 SDK 命令状态。 */
  private readonly directionReferenceResolver: LinearComponentPatternDirectionReferenceResolver;
  /** 独立旋转轴继续遵守既有线性参考规则，不随传播方向扩展。 */
  private readonly linearReferenceResolver: MoveAlongEntityReferenceResolver;
  /** 当前命令状态。 */
  private state: LinearComponentPatternState = createDefaultState();
  /** 选择变化监听器集合。 */
  private readonly selectionChangeListeners: Set<LinearComponentPatternSelectionChangeListener> = new Set();
  /**
   * 方向 1 参考对象运行时活引用。
   *
   * 设计说明：
   * 1. 只存 UUID 时，代理对象被 hover 系统回收后 scene.getObjectByProperty 找不到对象；
   * 2. 存活引用可保证高亮恢复时始终拿到真实对象，与 MoveComponentController 保持一致。
   */
  private direction1ReferenceObject: THREE.Object3D | null = null;

  /**
   * 方向 1 旋转实例轴对象运行时活引用。
   */
  private direction1RotationAxisObject: THREE.Object3D | null = null;

  /**
   * 方向 2 参考对象运行时活引用。
   */
  private direction2ReferenceObject: THREE.Object3D | null = null;

  /**
   * 方向 1 到参考模式的参考几何对象运行时活引用。
   */
  private direction1ReferenceGeometryObject: THREE.Object3D | null = null;

  /**
   * 方向 1 到参考模式的所选参考对象运行时活引用。
   */
  private direction1SelectedReferenceObject: THREE.Object3D | null = null;

  /**
   * 方向 2 到参考模式的参考几何对象运行时活引用。
   */
  private direction2ReferenceGeometryObject: THREE.Object3D | null = null;

  /**
   * 方向 2 到参考模式的所选参考对象运行时活引用。
   */
  private direction2SelectedReferenceObject: THREE.Object3D | null = null;

  /**
   * 创建线性零部件阵列控制器。
   *
   * @param viewer Viewer 实例。
   * @param previewRenderer 预览渲染器（可选，未传时内部自动创建）。
   */
  public constructor(
    viewer: Viewer,
    previewRenderer?: LinearComponentPatternPreviewRenderer,
  ) {
    this.viewer = viewer;
    this.linearReferenceResolver = new MoveAlongEntityReferenceResolver(viewer);
    this.directionReferenceResolver = new LinearComponentPatternDirectionReferenceResolver(this.linearReferenceResolver);
    this.previewRenderer = previewRenderer ?? new LinearComponentPatternPreviewRenderer(viewer);
    this.componentDisplayNameResolver = new LinearComponentPatternComponentDisplayNameResolver(
      viewer.scene,
      objectId => viewer.findSceneObjectById(objectId),
    );
  }

  /**
   * 打开命令。
   */
  public open(): void {
    // 打开命令时只切换生命周期，引用输入槽位由 PropertyManager 聚焦事件统一激活。
    this.state = {
      ...this.state,
      lifecycle: LinearComponentPatternLifecycle.Open,
    };
    this.viewer.requestRender?.();
  }

  /**
   * 判断命令是否激活。
   *
   * @returns 命令激活时返回 `true`。
   */
  public isActive(): boolean {
    return this.state.lifecycle === LinearComponentPatternLifecycle.Open;
  }

  /**
   * 获取 ViewModel。
   *
   * @returns 当前 ViewModel 快照。
   */
  public getViewModel(): LinearComponentPatternViewModel {
    return {
      state: this.cloneState(this.state),
    };
  }


  /**
   * 创建可持久化的阵列定义快照。
   *
   * 设计说明：
   * 1. 提交记录必须保存创建时的参数，才能从特征树执行“编辑特征”；
   * 2. 快照进入已提交状态后不应携带正在打开的命令生命周期，避免树节点误判为仍处于命令态；
   * 3. 数组字段必须复制，防止后续 PropertyManager 修改当前状态时反向污染历史定义。
   *
   * @returns 阵列定义快照。
   */
  public createDefinitionSnapshot(): LinearComponentPatternDefinition {
    return {
      state: {
        ...this.cloneState(this.state),
        lifecycle: LinearComponentPatternLifecycle.Closed,
      },
    };
  }

  /**
   * 从已提交阵列定义恢复编辑会话。
   *
   * 设计说明：
   * 1. SolidWorks 的“编辑特征”会打开原特征的 PropertyManager 并恢复原参数；
   * 2. PuppyCAD 通过稳定引用描述符重建运行时对象，不保存任何旧 Three.js 引用；
   * 3. 若必要引用丢失，直接返回失败，让上层提示用户，避免生成与原特征脱钩的阵列。
   *
   * @param definition 已提交阵列定义。
   * @returns 恢复成功时返回 `true`。
   */
  public restoreDefinition(definition: LinearComponentPatternDefinition): boolean {
    const nextState = this.cloneState(definition.state);
    // 包内对象可能仍存活，但已被项目 occurrence 接管；必须在预览/求解之前统一定义中的组件身份。
    const componentObjectIds = this.resolveComponentSourceObjectIds(nextState.componentObjectIds);
    const direction1ReferenceObject = this.resolveRequiredReferenceObject(
      nextState.direction1ReferenceDescriptor,
      nextState.direction1ReferenceObjectId,
    );
    if (!direction1ReferenceObject || !componentObjectIds) {
      return false;
    }

    const direction1RotationAxisObject = this.resolveOptionalReferenceObject(
      nextState.direction1RotationAxisDescriptor,
      nextState.direction1RotationAxisObjectId,
    );
    const direction1ReferenceGeometryObject = this.resolveOptionalReferenceObject(
      nextState.direction1ReferenceGeometryDescriptor,
      nextState.direction1ReferenceGeometryObjectId,
    );
    const direction1SelectedReferenceObject = this.resolveOptionalReferenceObject(
      nextState.direction1SelectedReferenceDescriptor,
      nextState.direction1SelectedReferenceObjectId,
    );
    const direction2ReferenceObject = this.resolveOptionalReferenceObject(
      nextState.direction2ReferenceDescriptor,
      nextState.direction2ReferenceObjectId,
    );
    const direction2ReferenceGeometryObject = this.resolveOptionalReferenceObject(
      nextState.direction2ReferenceGeometryDescriptor,
      nextState.direction2ReferenceGeometryObjectId,
    );
    const direction2SelectedReferenceObject = this.resolveOptionalReferenceObject(
      nextState.direction2SelectedReferenceDescriptor,
      nextState.direction2SelectedReferenceObjectId,
    );

    if (
      direction1RotationAxisObject === false
      || direction1ReferenceGeometryObject === false
      || direction1SelectedReferenceObject === false
      || direction2ReferenceObject === false
      || direction2ReferenceGeometryObject === false
      || direction2SelectedReferenceObject === false
    ) {
      return false;
    }

    // 历史/离线定义恢复同样不能接受无法求解的方向；全部校验后才原子替换状态。
    if (!this.directionReferenceResolver.canUseAsReference(direction1ReferenceObject)
      || (direction1RotationAxisObject && !this.linearReferenceResolver.canUseAsReference(direction1RotationAxisObject))
      || (direction2ReferenceObject && !this.directionReferenceResolver.canUseAsReference(direction2ReferenceObject))) {
      return false;
    }
    this.direction1ReferenceObject = direction1ReferenceObject;
    this.direction1RotationAxisObject = direction1RotationAxisObject;
    this.direction1ReferenceGeometryObject = direction1ReferenceGeometryObject;
    this.direction1SelectedReferenceObject = direction1SelectedReferenceObject;
    this.direction2ReferenceObject = direction2ReferenceObject;
    this.direction2ReferenceGeometryObject = direction2ReferenceGeometryObject;
    this.direction2SelectedReferenceObject = direction2SelectedReferenceObject;
    this.state = {
      ...nextState,
      componentObjectIds,
      lifecycle: LinearComponentPatternLifecycle.Open,
      activeSelectionSlot: LinearComponentPatternSelectionSlot.Direction1Reference,
      direction1ReferenceObjectId: direction1ReferenceObject.uuid,
      direction1ReferenceDescriptor: nextState.direction1ReferenceDescriptor
        ?? this.createReferenceDescriptor(direction1ReferenceObject),
      direction1RotationAxisObjectId: direction1RotationAxisObject?.uuid ?? null,
      direction1RotationAxisDescriptor: direction1RotationAxisObject
        ? nextState.direction1RotationAxisDescriptor ?? this.createReferenceDescriptor(direction1RotationAxisObject)
        : null,
      direction1ReferenceGeometryObjectId: direction1ReferenceGeometryObject?.uuid ?? null,
      direction1ReferenceGeometryDescriptor: direction1ReferenceGeometryObject
        ? nextState.direction1ReferenceGeometryDescriptor ?? this.createReferenceDescriptor(direction1ReferenceGeometryObject)
        : null,
      direction1SelectedReferenceObjectId: direction1SelectedReferenceObject?.uuid ?? null,
      direction1SelectedReferenceDescriptor: direction1SelectedReferenceObject
        ? nextState.direction1SelectedReferenceDescriptor ?? this.createReferenceDescriptor(direction1SelectedReferenceObject)
        : null,
      direction2ReferenceObjectId: direction2ReferenceObject?.uuid ?? null,
      direction2ReferenceDescriptor: direction2ReferenceObject
        ? nextState.direction2ReferenceDescriptor ?? this.createReferenceDescriptor(direction2ReferenceObject)
        : null,
      direction2ReferenceGeometryObjectId: direction2ReferenceGeometryObject?.uuid ?? null,
      direction2ReferenceGeometryDescriptor: direction2ReferenceGeometryObject
        ? nextState.direction2ReferenceGeometryDescriptor ?? this.createReferenceDescriptor(direction2ReferenceGeometryObject)
        : null,
      direction2SelectedReferenceObjectId: direction2SelectedReferenceObject?.uuid ?? null,
      direction2SelectedReferenceDescriptor: direction2SelectedReferenceObject
        ? nextState.direction2SelectedReferenceDescriptor ?? this.createReferenceDescriptor(direction2SelectedReferenceObject)
        : null,
    };
    this.notifyRestoredDirectionReferenceSelection(
      LinearComponentPatternSelectionSlot.Direction1Reference,
      direction1ReferenceObject,
      this.state.direction1ReferenceDisplayName,
    );
    if (direction2ReferenceObject) {
      this.notifyRestoredDirectionReferenceSelection(
        LinearComponentPatternSelectionSlot.Direction2Reference,
        direction2ReferenceObject,
        this.state.direction2ReferenceDisplayName,
      );
    }
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
    return true;
  }


  /**
   * 设置当前激活选择槽位。
   *
   * @param slot 目标槽位。
   */
  public setActiveSelectionSlot(slot: LinearComponentPatternSelectionSlot): void {
    const previousSlot = this.state.activeSelectionSlot;
    // 选择槽位由 PropertyManager 控制，cad-core 只保存语义，不感知具体 UI 组件。
    this.state = {
      ...this.state,
      activeSelectionSlot: slot,
    };


    // 跳过实例小球仅在对应输入槽位激活时显示，因此切换进出该槽位都要刷新预览。
    if (
      previousSlot === LinearComponentPatternSelectionSlot.SkippedInstances
      || slot === LinearComponentPatternSelectionSlot.SkippedInstances
    ) {
      this.triggerPreviewUpdate();
      this.viewer.requestRender?.();
    }

  }

  /**
   * 读取方向 1 参考对象活引用。
   *
   * @returns 参考对象；未选择时返回 `null`。
   */
  public getDirection1ReferenceObject(): THREE.Object3D | null {
    return this.direction1ReferenceObject;
  }

  /**
   * 读取方向 2 参考对象活引用。
   *
   * @returns 参考对象；未选择时返回 `null`。
   */
  public getDirection2ReferenceObject(): THREE.Object3D | null {
    return this.direction2ReferenceObject;
  }

  /**
   * 按槽位读取引用对象活引用。
   *
   * @param slot 引用槽位。
   * @returns 对应活引用；未选择或非引用槽位时返回 `null`。
   */
  public getReferenceObjectBySlot(slot: LinearComponentPatternSelectionSlot): THREE.Object3D | null {
    if (slot === LinearComponentPatternSelectionSlot.Direction1Reference) {
      return this.direction1ReferenceObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis) {
      return this.direction1RotationAxisObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry) {
      return this.direction1ReferenceGeometryObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1SelectedReference) {
      return this.direction1SelectedReferenceObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2Reference) {
      return this.direction2ReferenceObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry) {
      return this.direction2ReferenceGeometryObject;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2SelectedReference) {
      return this.direction2SelectedReferenceObject;
    }
    return null;
  }

  /**
   * 获取当前跳过实例选择小球列表。
   *
   * @returns marker 列表。
   */
  public getSkipInstanceMarkerObjects(): THREE.Sprite[] {
    return this.previewRenderer.getSkipInstanceMarkerObjects();
  }

  /**
   * 根据跳过实例选择小球解析逻辑实例键。
   *
   * @param object 当前对象。
   * @returns 逻辑实例键；不是 marker 时返回 `null`。
   */
  public resolveSkipInstanceKeyByMarker(object: THREE.Object3D): string | null {
    return this.previewRenderer.resolveSkipInstanceKeyByMarker(object);
  }

  /**
   * 写入当前槽位的选择对象。
   *
   * 设计说明：
   * 本方法只负责状态更新与 UI 通知，不触发预览计算。
   * 预览计算由选择层（EntitySelectionHandler）在调用本方法后显式调用
   * `triggerPreviewUpdate()`，确保"计算"的驱动权在选择层而非 UI 层。
   *
   * @param object 选择对象。
   * @returns 是否接受写入；拒绝时不替换既有引用、不派发选择变更。
   */
  public setSelectionForActiveSlot(object: THREE.Object3D): boolean {
    const slot = this.state.activeSelectionSlot;
    // 传播方向允许圆轴；旋转轴槽仍由独立的线性参考规则校验。
    const referenceResolver = slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
      ? this.linearReferenceResolver : this.directionReferenceResolver;
    // 在写入活引用、稳定描述符和通知之前完成几何校验，拒绝时保留旧预览状态。
    if (LINEAR_COMPONENT_PATTERN_DIRECTION_REFERENCE_SLOTS.includes(slot)
      && !referenceResolver.canUseAsReference(object)) return false;
    if (this.storeReferenceSelection(slot, object)) {
      this.notifySelectionChange({
        activeSelectionSlot: slot,
        objectId: object.uuid,
        displayName: this.resolveObjectDisplayName(object),
      });
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.ComponentsToPattern) {
      const componentObject = this.resolveComponentSourceObject(object);
      if (!componentObject) {
        return false;
      }
      // 显示节点、拓扑代理和组件根必须先归一再去重，避免同一 occurrence 生成多份定义。
      const nextIds = this.appendUniqueObjectId(this.state.componentObjectIds, componentObject.uuid);
      this.state = {
        ...this.state,
        componentObjectIds: nextIds,
      };
      // 通知 UI 层零部件列表已变更，使标签立即同步，不依赖轮询。
      this.notifySelectionChange({
        activeSelectionSlot: slot,
        objectId: componentObject.uuid,
        displayName: this.componentDisplayNameResolver.resolve(componentObject),
        componentObjectIds: nextIds,
      });
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.SkippedInstances) {

      // 跳过实例只接受预览 marker；命中后切换逻辑实例可见性并同步回写 UI。
      const instanceKey = this.previewRenderer.resolveSkipInstanceKeyByMarker(object);
      if (instanceKey) {
        this.toggleSkippedInstanceKey(instanceKey);
      }

      return Boolean(instanceKey);
    }
    if (slot === LinearComponentPatternSelectionSlot.ModifiedInstances) {
      // 修改实例与跳过实例分开保存，避免后续偏移尺寸编辑时混用状态。
      this.state = {
        ...this.state,
        modifiedInstanceObjectIds: this.appendUniqueObjectId(this.state.modifiedInstanceObjectIds, object.uuid),
      };
      this.viewer.requestRender?.();
      return true;
    }
    return false;
  }

  /**
   * 按控制器保存的组件对象 ID 解析 PropertyManager 显示名称。
   *
   * @param objectId 组件变换根 UUID。
   * @returns 组件 occurrence 名称；对象已失效时回退原 ID。
   */
  public resolveComponentDisplayNameByObjectId(objectId: string): string {
    return this.componentDisplayNameResolver.resolveByObjectId(objectId);
  }


  /**
   * 移除单个要阵列的源零部件。
   *
   * @param objectId 待移除零部件对象 ID。
   */
  public removeComponentObjectId(objectId: string): void {
    if (!this.state.componentObjectIds.includes(objectId)) {
      return;
    }

    const nextIds = this.state.componentObjectIds.filter((currentId) => currentId !== objectId);
    this.state = {
      ...this.state,
      componentObjectIds: nextIds,
    };
    this.notifySelectionChange({
      activeSelectionSlot: LinearComponentPatternSelectionSlot.ComponentsToPattern,
      objectId: null,
      displayName: '',
      componentObjectIds: nextIds,
    });
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
  }

  /**
   * 清空全部要阵列的源零部件。
   */
  public clearComponentObjectIds(): void {
    if (this.state.componentObjectIds.length === 0) {
      return;
    }

    this.state = {
      ...this.state,
      componentObjectIds: [],
    };
    this.notifySelectionChange({
      activeSelectionSlot: LinearComponentPatternSelectionSlot.ComponentsToPattern,
      objectId: null,
      displayName: '',
      componentObjectIds: [],
    });
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
  }


  /**
   * 切换单个跳过实例键。
   *
   * @param instanceKey 逻辑实例键。
   */
  public toggleSkippedInstanceKey(instanceKey: string): void {
    this.toggleSkippedInstanceKeys([instanceKey]);
  }

  /**
   * 批量切换跳过实例键。
   *
   * @param instanceKeys 逻辑实例键列表。
   */
  public toggleSkippedInstanceKeys(instanceKeys: readonly string[]): void {

    const uniqueKeys = Array.from(new Set(instanceKeys.filter((item) => item.length > 0)));
    if (uniqueKeys.length === 0) {
      return;
    }

    const nextSkippedInstanceKeySet = new Set(this.state.skippedInstanceKeys);
    uniqueKeys.forEach((instanceKey) => {
      if (nextSkippedInstanceKeySet.has(instanceKey)) {
        nextSkippedInstanceKeySet.delete(instanceKey);
        return;
      }
      nextSkippedInstanceKeySet.add(instanceKey);
    });

    this.state = {
      ...this.state,
      skippedInstanceKeys: Array.from(nextSkippedInstanceKeySet),
    };
    this.notifySelectionChange({
      activeSelectionSlot: LinearComponentPatternSelectionSlot.SkippedInstances,
      objectId: null,
      displayName: '',
      skippedInstanceKeys: this.state.skippedInstanceKeys,
    });
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();

  }

  /**
   * 移除单个跳过实例键。
   *
   * @param instanceKey 逻辑实例键。
   */
  public removeSkippedInstanceKey(instanceKey: string): void {
    this.removeSkippedInstanceKeys([instanceKey]);
  }

  /**
   * 批量移除跳过实例键。
   *
   * @param instanceKeys 逻辑实例键列表。
   */
  public removeSkippedInstanceKeys(instanceKeys: readonly string[]): void {

    const uniqueKeys = Array.from(new Set(instanceKeys.filter((item) => item.length > 0)));
    if (uniqueKeys.length === 0 || this.state.skippedInstanceKeys.length === 0) {
      return;
    }

    const nextSkippedInstanceKeys = this.state.skippedInstanceKeys
      .filter((instanceKey) => !uniqueKeys.includes(instanceKey));
    if (nextSkippedInstanceKeys.length === this.state.skippedInstanceKeys.length) {
      return;
    }

    this.state = {
      ...this.state,
      skippedInstanceKeys: nextSkippedInstanceKeys,
    };
    this.notifySelectionChange({
      activeSelectionSlot: LinearComponentPatternSelectionSlot.SkippedInstances,
      objectId: null,
      displayName: '',
      skippedInstanceKeys: this.state.skippedInstanceKeys,
    });
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();

  }

  /**
   * 清空全部跳过实例键。
   */
  public clearSkippedInstanceKeys(): void {

    if (this.state.skippedInstanceKeys.length === 0) {
      return;
    }

    this.state = {
      ...this.state,
      skippedInstanceKeys: [],
    };
    this.notifySelectionChange({
      activeSelectionSlot: LinearComponentPatternSelectionSlot.SkippedInstances,
      objectId: null,
      displayName: '',
      skippedInstanceKeys: [],
    });
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();

  }

  /**
   * 清空当前激活方向槽位的引用选择。
   *
   * 设计说明：根据当前激活槽位判断清除方向 1 还是方向 2 的引用，
   * 避免清错方向（UI 在调用前已激活对应槽位）。
   */
  public clearReferenceInputSelection(): void {
    const slot = this.state.activeSelectionSlot;
    if (this.clearReferenceSelection(slot)) {
      this.notifySelectionChange({ activeSelectionSlot: slot, objectId: null, displayName: '' });
      if (
        slot === LinearComponentPatternSelectionSlot.Direction1Reference
        || slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis
        || slot === LinearComponentPatternSelectionSlot.Direction2Reference
      ) {
        this.triggerPreviewUpdate();
      }
    }
    this.viewer.requestRender?.();
  }

  /**
   * 更新方向 1 间距与实例数。
   *
   * @param spacing 间距（米制渲染空间单位）。
   * @param instances 实例数（含源实例）。
   */
  public updateDirection1Params(spacing: number, instances: number): void {

    this.state = {
      ...this.state,
      direction1Spacing: spacing,
      direction1Instances: Math.max(1, Math.floor(instances)),
    };
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();

  }

  /**
   * 更新方向 2 间距与实例数。
   *
   * @param spacing 间距（米制渲染空间单位）。
   * @param instances 实例数（含源实例）。
   */
  public updateDirection2Params(spacing: number, instances: number): void {

    this.state = {
      ...this.state,
      direction2Spacing: spacing,
      direction2Instances: Math.max(1, Math.floor(instances)),
    };
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();

  }

  /**
   * 更新方向 1 定义状态。
   *
   * @param definition 方向 1 定义状态。
   */
  public updateDirection1Definition(definition: LinearComponentPatternDirectionDefinitionState): void {
    const nextFixedRotationAxis = definition.fixedRotationAxis ?? this.state.direction1FixedRotationAxis;
    this.state = {
      ...this.state,
      direction1Mode: definition.mode,
      direction1Spacing: Math.max(0, definition.spacing),
      direction1Instances: Math.max(1, Math.floor(definition.instances)),
      direction1OffsetDistance: Math.max(0, definition.offsetDistance),
      direction1ReferenceOffsetMode: definition.referenceOffsetMode,
      direction1SetSpacing: definition.setSpacing,
      direction1SetInstances: definition.setInstances,

      direction1ReverseDirection: definition.reverseDirection ?? this.state.direction1ReverseDirection,


      // 旋转实例是方向 1 的扩展参数；未传入时保留旧值，避免旧 UI 同步基础参数时关闭旋转设置。
      direction1RotateInstances: definition.rotateInstances ?? this.state.direction1RotateInstances,
      direction1RotationAngle: definition.rotationAngle ?? this.state.direction1RotationAngle,
      direction1ReverseRotation: definition.reverseRotation ?? this.state.direction1ReverseRotation,
      direction1FixedRotationAxis: nextFixedRotationAxis,
      direction1AlignToSeed: nextFixedRotationAxis
        ? definition.alignToSeed ?? this.state.direction1AlignToSeed
        : false,
      direction1RotationReferencePoint: definition.rotationReferencePoint ?? this.state.direction1RotationReferencePoint,

    };
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
  }


  /**
   * 更新方向 1 旋转实例参数。
   *
   * @param rotateInstances 是否启用旋转实例。
   * @param rotationAngle 旋转角度增量（角度制）。
   * @param reverseRotation 是否反转旋转方向。
   * @param fixedRotationAxis 是否固定旋转轴。
   * @param alignToSeed 是否对齐到源。
   * @param rotationReferencePoint 对齐到源时使用的参考点。
   */
  public updateDirection1RotationParams(
    rotateInstances: boolean,
    rotationAngle: number,
    reverseRotation: boolean,
    fixedRotationAxis: boolean = true,
    alignToSeed: boolean = false,
    rotationReferencePoint: LinearComponentPatternRotationReferencePoint = LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
  ): void {
    this.state = {
      ...this.state,
      direction1RotateInstances: rotateInstances,
      direction1RotationAngle: rotationAngle,
      direction1ReverseRotation: reverseRotation,
      direction1FixedRotationAxis: fixedRotationAxis,
      direction1AlignToSeed: fixedRotationAxis ? alignToSeed : false,
      direction1RotationReferencePoint: rotationReferencePoint,
    };
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
  }


  /**
   * 更新方向 2 定义状态。
   *
   * @param definition 方向 2 定义状态。
   */
  public updateDirection2Definition(definition: LinearComponentPatternDirectionDefinitionState): void {
    this.state = {
      ...this.state,
      direction2Mode: definition.mode,
      direction2Spacing: Math.max(0, definition.spacing),
      direction2Instances: Math.max(1, Math.floor(definition.instances)),
      direction2OffsetDistance: Math.max(0, definition.offsetDistance),
      direction2ReferenceOffsetMode: definition.referenceOffsetMode,
      direction2SetSpacing: definition.setSpacing,
      direction2SetInstances: definition.setInstances,

      direction2ReverseDirection: definition.reverseDirection ?? this.state.direction2ReverseDirection,


      direction2PatternSeedOnly: definition.patternSeedOnly ?? this.state.direction2PatternSeedOnly,

    };
    this.triggerPreviewUpdate();
    this.viewer.requestRender?.();
  }

  /**
   * 监听选择变化。
   *
   * @param listener 监听器。
   * @returns 取消监听函数。
   */
  public onSelectionChange(listener: LinearComponentPatternSelectionChangeListener): () => void {
    this.selectionChangeListeners.add(listener);
    return () => {
      this.selectionChangeListeners.delete(listener);
    };
  }

  /**
   * 提交命令。
   *
   * 设计说明：
   * 1. 提交时先将预览参数转换为实例偏移数据；
   * 2. 再交给 `LinearComponentPatternManager` 落地为实际对象并挂载；
   * 3. 最后统一执行 `cancel()` 收口预览与命令态。
   */
  public commit(): void {

    const commitData = this.buildCommitData();
    if (commitData && commitData.instances.length > 0) {
      this.viewer.linearComponentPatternManager?.commitPattern(
        commitData,
        this.createDefinitionSnapshot(),
      );
    }

    this.cancel();
  }

  /**
   * 构建线性零部件阵列提交数据。
   *
   * @returns 提交数据；当前状态不足以提交时返回 `null`。
   */
  public buildCommitData(): LinearComponentPatternCommitData | null {
    if (this.state.componentObjectIds.length === 0 || !this.direction1ReferenceObject) {
      return null;
    }

    const offsets = this.previewRenderer.computeInstanceOffsets({
      direction1ReferenceObject: this.direction1ReferenceObject,
      direction1ReferenceGeometryObject: this.direction1ReferenceGeometryObject,
      direction1SelectedReferenceObject: this.direction1SelectedReferenceObject,

      direction1ReverseDirection: this.state.direction1ReverseDirection,


      direction1RotationAxisObject: this.direction1RotationAxisObject,
      direction1RotateInstances: this.state.direction1RotateInstances,
      direction1RotationAngle: this.state.direction1RotationAngle,
      direction1ReverseRotation: this.state.direction1ReverseRotation,
      direction1FixedRotationAxis: this.state.direction1FixedRotationAxis,
      direction1AlignToSeed: this.state.direction1AlignToSeed,
      direction1RotationReferencePoint: this.state.direction1RotationReferencePoint,

      direction1Mode: this.state.direction1Mode,
      direction1Spacing: this.state.direction1Spacing,
      direction1Instances: this.state.direction1Instances,
      direction1OffsetDistance: this.state.direction1OffsetDistance,
      direction1ReferenceOffsetMode: this.state.direction1ReferenceOffsetMode,
      direction1SetSpacing: this.state.direction1SetSpacing,
      direction1SetInstances: this.state.direction1SetInstances,
      direction2ReferenceObject: this.direction2ReferenceObject,
      direction2ReferenceGeometryObject: this.direction2ReferenceGeometryObject,
      direction2SelectedReferenceObject: this.direction2SelectedReferenceObject,

      direction2ReverseDirection: this.state.direction2ReverseDirection,

      direction2Mode: this.state.direction2Mode,
      direction2Spacing: this.state.direction2Spacing,
      direction2Instances: this.state.direction2Instances,
      direction2OffsetDistance: this.state.direction2OffsetDistance,
      direction2ReferenceOffsetMode: this.state.direction2ReferenceOffsetMode,
      direction2SetSpacing: this.state.direction2SetSpacing,
      direction2SetInstances: this.state.direction2SetInstances,
      direction2PatternSeedOnly: this.state.direction2PatternSeedOnly,
      componentObjectIds: [...this.state.componentObjectIds],
      skippedInstanceKeys: [...this.state.skippedInstanceKeys],
    });

    if (offsets.length === 0) {
      return null;
    }

    return {
      instances: offsets.map((item) => ({
        sourceObjectId: item.sourceObjectId,
        worldOffset: {
          x: item.worldOffset.x,
          y: item.worldOffset.y,
          z: item.worldOffset.z,
        },
        ...(item.worldTransform
          ? {
            worldTransform: {
              position: {
                x: item.worldTransform.position.x,
                y: item.worldTransform.position.y,
                z: item.worldTransform.position.z,
              },
              quaternion: {
                x: item.worldTransform.quaternion.x,
                y: item.worldTransform.quaternion.y,
                z: item.worldTransform.quaternion.z,
                w: item.worldTransform.quaternion.w,
              },
              scale: {
                x: item.worldTransform.scale.x,
                y: item.worldTransform.scale.y,
                z: item.worldTransform.scale.z,
              },
            },
          }
          : {}),
      })),
    };
  }

  /**
   * 取消命令。
   */
  public cancel(): void {
    this.previewRenderer.clear();
    // 清除方向箭头（方向1、方向2）。
    this.viewer.patternDirectionArrowManager.clearAll();
    // 清除选中高亮状态，避免退出后高亮残留。
    this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
    // 取消命令时同步清空两路活引用，避免对象悬挂。
    this.direction1ReferenceObject = null;
    this.direction1RotationAxisObject = null;
    this.direction2ReferenceObject = null;
    this.direction1ReferenceGeometryObject = null;
    this.direction1SelectedReferenceObject = null;
    this.direction2ReferenceGeometryObject = null;
    this.direction2SelectedReferenceObject = null;
    this.state = createDefaultState();
    this.viewer.requestRender?.();
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    this.cancel();
    this.previewRenderer.dispose();
    this.selectionChangeListeners.clear();
  }

  /**
   * 追加唯一对象 ID。
   *
   * @param objectIds 原对象 ID 列表。
   * @param objectId 待追加 ID。
   * @returns 去重后的对象 ID 列表。
   */
  private appendUniqueObjectId(objectIds: readonly string[], objectId: string): string[] {
    return Array.from(new Set([...objectIds, objectId]));
  }


  /**
   * 克隆阵列状态对象。
   *
   * @param state 来源状态。
   * @returns 独立状态副本。
   */
  private cloneState(state: LinearComponentPatternState): LinearComponentPatternState {
    return {
      ...state,
      direction1ReferenceDescriptor: this.cloneReferenceDescriptor(state.direction1ReferenceDescriptor ?? null),
      direction1RotationAxisDescriptor: this.cloneReferenceDescriptor(state.direction1RotationAxisDescriptor ?? null),
      direction1ReferenceGeometryDescriptor: this.cloneReferenceDescriptor(
        state.direction1ReferenceGeometryDescriptor ?? null,
      ),
      direction1SelectedReferenceDescriptor: this.cloneReferenceDescriptor(
        state.direction1SelectedReferenceDescriptor ?? null,
      ),
      direction2ReferenceDescriptor: this.cloneReferenceDescriptor(state.direction2ReferenceDescriptor ?? null),
      direction2ReferenceGeometryDescriptor: this.cloneReferenceDescriptor(
        state.direction2ReferenceGeometryDescriptor ?? null,
      ),
      direction2SelectedReferenceDescriptor: this.cloneReferenceDescriptor(
        state.direction2SelectedReferenceDescriptor ?? null,
      ),
      componentObjectIds: [...state.componentObjectIds],
      skippedInstanceKeys: [...state.skippedInstanceKeys],
      modifiedInstanceObjectIds: [...state.modifiedInstanceObjectIds],
    };
  }

  /**
   * 克隆引用描述符。
   *
   * @param descriptor 来源描述符。
   * @returns 独立描述符副本。
   */
  private cloneReferenceDescriptor(
    descriptor: LinearComponentPatternReferenceDescriptor | null,
  ): LinearComponentPatternReferenceDescriptor | null {
    if (!descriptor) {
      return null;
    }
    if (descriptor.kind === LinearComponentPatternReferenceDescriptorKind.TopologyProxy) {
      return {
        ...descriptor,
        assemblyTopologyRef: this.cloneAssemblyTopologyRef(descriptor.assemblyTopologyRef ?? null),
      };
    }
    return { ...descriptor };
  }

  /**
   * 克隆 imported 装配拓扑引用。
   *
   * @param ref 来源引用。
   * @returns 独立引用副本。
   */
  private cloneAssemblyTopologyRef(ref: AssemblyTopologySelectionRef | null): AssemblyTopologySelectionRef | null {
    if (!ref) {
      return null;
    }
    return {
      ...ref,
      adjacentFaceRefs: ref.adjacentFaceRefs ? [...ref.adjacentFaceRefs] : undefined,
    };
  }

  /**
   * 按稳定引用描述符解析必选参考对象。
   *
   * @param descriptor 稳定引用描述符。
   * @param objectId 对象 ID。
   * @returns 场景对象；描述符和旧 ID 均无法解析时返回 `null`。
   */
  private resolveRequiredReferenceObject(
    descriptor: LinearComponentPatternReferenceDescriptor | null,
    objectId: string | null,
  ): THREE.Object3D | null {
    return this.resolveReferenceObject(descriptor, objectId);
  }

  /**
   * 按稳定引用描述符解析可选参考对象。
   *
   * @param descriptor 稳定引用描述符。
   * @param objectId 对象 ID。
   * @returns 对象存在时返回对象；未配置时返回 `null`；配置但丢失时返回 `false`。
   */
  private resolveOptionalReferenceObject(
    descriptor: LinearComponentPatternReferenceDescriptor | null,
    objectId: string | null,
  ): THREE.Object3D | null | false {
    if (!descriptor && !objectId) {
      return null;
    }
    return this.resolveReferenceObject(descriptor, objectId) ?? false;
  }

  /**
   * 按稳定描述符或旧对象 ID 解析参考对象。
   *
   * 设计说明：
   * 1. 新定义优先使用拓扑描述符，避免依赖点击时动态创建的代理 uuid；
   * 2. 旧定义没有描述符时，仍尝试从场景与结果体代理缓存中按 uuid 找回；
   * 3. 这样既修复新提交的阵列，也给当前会话里旧记录一个恢复机会。
   *
   * @param descriptor 稳定引用描述符。
   * @param objectId 旧对象 ID。
   * @returns 解析到的对象；失败时返回 `null`。
   */
  private resolveReferenceObject(
    descriptor: LinearComponentPatternReferenceDescriptor | null,
    objectId: string | null,
  ): THREE.Object3D | null {
    if (descriptor?.kind === LinearComponentPatternReferenceDescriptorKind.TopologyProxy) {
      const topologyProxyObject = this.resolveTopologyProxyReference(descriptor);
      if (topologyProxyObject) {
        return topologyProxyObject;
      }
    }

    if (descriptor?.kind === LinearComponentPatternReferenceDescriptorKind.SceneObject) {
      const sceneObject = this.resolveSceneObjectByIdIncludingTopologyCache(descriptor.objectId);
      if (sceneObject) {
        return sceneObject;
      }
    }

    if (objectId) {
      return this.resolveSceneObjectByIdIncludingTopologyCache(objectId);
    }

    return null;
  }

  /**
   * 解析拓扑代理引用。
   *
   * @param descriptor 拓扑代理描述符。
   * @returns 重建后的点线面代理；失败时返回 `null`。
   */
  private resolveTopologyProxyReference(
    descriptor: Extract<
      LinearComponentPatternReferenceDescriptor,
      { kind: LinearComponentPatternReferenceDescriptorKind.TopologyProxy }
    >,
  ): THREE.Object3D | null {
    if (descriptor.assemblyTopologyRef) {
      const assemblyTopologyObject = resolveAssemblyTopologyReferenceObject(
        this.viewer,
        descriptor.assemblyTopologyRef,
      );
      if (assemblyTopologyObject) {
        assemblyTopologyObject.updateMatrixWorld(true);
        return assemblyTopologyObject;
      }
    }

    const hostMesh = this.resolveStaticFeatureResultMeshById(descriptor.hostObjectId);
    if (!hostMesh) {
      return null;
    }

    // 拓扑代理按需挂载，确保恢复后的对象拥有正确世界矩阵与后续交互生命周期。
    if (descriptor.topologyKind === LinearComponentPatternTopologyProxyKind.Face) {
      return hostMesh.attachFeatureFaceByIndex(descriptor.topologyIndex);
    }
    if (descriptor.topologyKind === LinearComponentPatternTopologyProxyKind.Edge) {
      return hostMesh.attachFeatureLineByIndex(descriptor.topologyIndex);
    }
    if (descriptor.topologyKind === LinearComponentPatternTopologyProxyKind.Vertex) {
      return hostMesh.attachFeaturePointByIndex(descriptor.topologyIndex);
    }

    return null;
  }

  /**
   * 按 ID 解析静态特征结果体。
   *
   * 设计说明：
   * 1. imported BRep pick host 由选择链动态创建，不能把 `imported-brep:*` ID 当作稳定引用；
   * 2. imported 拓扑恢复必须优先走 `AssemblyTopologySelectionRef`；
   * 3. 本方法仅服务原生结果体和旧定义兼容兜底。
   *
   * @param objectId 结果体对象 ID。
   * @returns 特征结果体；失败时返回 `null`。
   */
  private resolveStaticFeatureResultMeshById(objectId: string): FeatureResultMesh | null {
    const directObject = this.viewer.findSceneObjectById(objectId);
    if (directObject instanceof FeatureResultMesh && !isImportedModelTopologyHost(directObject)) {
      return directObject;
    }

    let targetMesh: FeatureResultMesh | null = null;
    this.viewer.scene.traverse((object) => {
      if (targetMesh || !(object instanceof FeatureResultMesh) || isImportedModelTopologyHost(object)) {
        return;
      }
      if (object.uuid === objectId || object.name === objectId || object.objectId === objectId) {
        targetMesh = object;
      }
    });
    return targetMesh;
  }

  /**
   * 按对象 ID 解析场景对象，并兼容结果体内部已缓存的动态拓扑代理。
   *
   * @param objectId 对象 ID。
   * @returns 场景对象；失败时返回 `null`。
   */
  private resolveSceneObjectByIdIncludingTopologyCache(objectId: string): THREE.Object3D | null {
    const sceneObject = this.viewer.findSceneObjectById(objectId);
    if (sceneObject) {
      return sceneObject;
    }

    let proxyObject: THREE.Object3D | null = null;
    this.viewer.scene.traverse((object) => {
      if (proxyObject || !(object instanceof FeatureResultMesh)) {
        return;
      }
      proxyObject = [
        ...object.getAllFeaturePoints(),
        ...object.getAllFeatureLines(),
        ...object.getAllFeatureFaces(),
      ].find((featureObject) => featureObject.uuid === objectId) ?? null;
    });
    return proxyObject;
  }

  /**
   * 将单个选择来源归一为最终组件根。
   *
   * 复用装配模块的权威解析器，与项目包重绑定共用 occurrence 解析规则；
   * 不按对象名称猜测，也不把失效拓扑代理作为普通 Mesh 继续传入求解。
   *
   * @param object 显示对象、拓扑代理或组件对象。
   * @returns 当前可写组件根；无法精确解析时返回 `null`。
   */
  private resolveComponentSourceObject(object: THREE.Object3D): THREE.Object3D | null {
    return resolveComponentInstanceTransformTarget(object, {
      scene: this.viewer.scene,
      getSceneObjectById: objectId => this.viewer.findSceneObjectById(objectId),
    });
  }

  /**
   * 原子解析并去重整组组件引用，防止定义和求解数据分别使用包内 UUID 与项目组件 UUID。
   *
   * @param objectIds 待恢复的组件来源 ID。
   * @returns 全部解析成功后的组件根 UUID；空输入或任意引用失效时返回 `null`。
   */
  private resolveComponentSourceObjectIds(objectIds: readonly string[]): string[] | null {
    const resolvedIds = new Set<string>();
    for (const objectId of objectIds) {
      const sourceObject = this.viewer.findSceneObjectById(objectId);
      const componentObject = sourceObject ? this.resolveComponentSourceObject(sourceObject) : null;
      if (!componentObject) {
        return null;
      }
      resolvedIds.add(componentObject.uuid);
    }
    return resolvedIds.size > 0 ? [...resolvedIds] : null;
  }

  /**
   * 为引用对象创建稳定描述符。
   *
   * @param object 引用对象。
   * @returns 稳定引用描述符。
   */
  private createReferenceDescriptor(object: THREE.Object3D): LinearComponentPatternReferenceDescriptor {
    if (isFeatureFace(object)) {
      const hostObjectId = object.getOwningResultMesh()?.objectId ?? object.objectId;
      return {
        kind: LinearComponentPatternReferenceDescriptorKind.TopologyProxy,
        topologyKind: LinearComponentPatternTopologyProxyKind.Face,
        hostObjectId,
        topologyIndex: object.faceIndex,
        persistentRef: object.persistentRef,
        assemblyTopologyRef: this.createAssemblyTopologyRef(object),
      };
    }

    if (isFeatureLine(object) && typeof object.edgeIndex === 'number' && object.objectId) {
      return {
        kind: LinearComponentPatternReferenceDescriptorKind.TopologyProxy,
        topologyKind: LinearComponentPatternTopologyProxyKind.Edge,
        hostObjectId: object.getOwningResultMesh()?.objectId ?? object.objectId,
        topologyIndex: object.edgeIndex,
        persistentRef: object.persistentRef ?? '',
        assemblyTopologyRef: this.createAssemblyTopologyRef(object),
      };
    }

    if (isFeaturePoint(object) && typeof object.vertexIndex === 'number' && object.objectId) {
      return {
        kind: LinearComponentPatternReferenceDescriptorKind.TopologyProxy,
        topologyKind: LinearComponentPatternTopologyProxyKind.Vertex,
        hostObjectId: object.getOwningResultMesh()?.objectId ?? object.objectId,
        topologyIndex: object.vertexIndex,
        persistentRef: object.persistentRef ?? '',
        assemblyTopologyRef: this.createAssemblyTopologyRef(object),
      };
    }

    return {
      kind: LinearComponentPatternReferenceDescriptorKind.SceneObject,
      objectId: object.uuid,
    };
  }

  /**
   * 为 imported BRep 拓扑代理创建装配拓扑稳定引用。
   *
   * 设计说明：
   * 1. imported BRep 的 pick host 由选择链按需创建，运行时 ID 不稳定；
   * 2. 现有装配拓扑引用已经封装了 assemblyNodeId、bodyBindingId 与子拓扑索引；
   * 3. 线性阵列复用该协议，避免另起一套 imported BRep 恢复逻辑。
   *
   * @param object 拓扑代理对象。
   * @returns 装配拓扑稳定引用；非 imported BRep 对象返回 `null`。
   */
  private createAssemblyTopologyRef(object: THREE.Object3D): AssemblyTopologySelectionRef | null {
    const topologyPick = resolveTopologySelectionPick(object);
    return resolveImportedAssemblyTopologySelectionRef(object, topologyPick);
  }

  /**
   * 通知编辑特征恢复出来的方向引用。
   *
   * 设计说明：
   * 1. 方向箭头由选择处理器统一创建，控制器不直接持有箭头几何逻辑；
   * 2. 编辑特征恢复不是一次鼠标点击，因此需要显式发出“引用已恢复”事件；
   * 3. 这样点击选择和编辑恢复都复用同一套方向箭头绘制入口。
   *
   * @param slot 方向引用槽位。
   * @param object 恢复出的方向引用对象。
   * @param displayName 方向引用显示名称。
   */
  private notifyRestoredDirectionReferenceSelection(
    slot: LinearComponentPatternSelectionSlot,
    object: THREE.Object3D,
    displayName: string,
  ): void {
    this.notifySelectionChange({
      activeSelectionSlot: slot,
      objectId: object.uuid,
      displayName,
    });
  }

  /**
   * 触发线性零部件阵列预览更新。
   *
   * 设计说明：
   * 可见性改为 public，由选择层（EntitySelectionHandler）在完成槽位写入后
   * 显式调用，确保"计算"的驱动权统一在选择层，不被 UI 层直接触发。
   * 数字参数更新路径（updateDirection1Params / updateDirection2Params）
   * 因无选择层对应路径，仍在方法内部自调用。
   */
  public triggerPreviewUpdate(): void {

    this.previewRenderer.update({
      direction1ReferenceObject: this.direction1ReferenceObject,
      direction1ReferenceGeometryObject: this.direction1ReferenceGeometryObject,
      direction1SelectedReferenceObject: this.direction1SelectedReferenceObject,

      direction1ReverseDirection: this.state.direction1ReverseDirection,


      direction1RotationAxisObject: this.direction1RotationAxisObject,
      direction1RotateInstances: this.state.direction1RotateInstances,
      direction1RotationAngle: this.state.direction1RotationAngle,
      direction1ReverseRotation: this.state.direction1ReverseRotation,
      direction1FixedRotationAxis: this.state.direction1FixedRotationAxis,
      direction1AlignToSeed: this.state.direction1AlignToSeed,
      direction1RotationReferencePoint: this.state.direction1RotationReferencePoint,

      direction1Mode: this.state.direction1Mode,
      direction1Spacing: this.state.direction1Spacing,
      direction1Instances: this.state.direction1Instances,
      direction1OffsetDistance: this.state.direction1OffsetDistance,
      direction1ReferenceOffsetMode: this.state.direction1ReferenceOffsetMode,
      direction1SetSpacing: this.state.direction1SetSpacing,
      direction1SetInstances: this.state.direction1SetInstances,
      direction2ReferenceObject: this.direction2ReferenceObject,
      direction2ReferenceGeometryObject: this.direction2ReferenceGeometryObject,
      direction2SelectedReferenceObject: this.direction2SelectedReferenceObject,

      direction2ReverseDirection: this.state.direction2ReverseDirection,

      direction2Mode: this.state.direction2Mode,
      direction2Spacing: this.state.direction2Spacing,
      direction2Instances: this.state.direction2Instances,
      direction2OffsetDistance: this.state.direction2OffsetDistance,
      direction2ReferenceOffsetMode: this.state.direction2ReferenceOffsetMode,
      direction2SetSpacing: this.state.direction2SetSpacing,
      direction2SetInstances: this.state.direction2SetInstances,
      direction2PatternSeedOnly: this.state.direction2PatternSeedOnly,
      componentObjectIds: [...this.state.componentObjectIds],
      skippedInstanceKeys: [...this.state.skippedInstanceKeys],
      showSkipInstanceMarkers: this.state.activeSelectionSlot === LinearComponentPatternSelectionSlot.SkippedInstances,
    });

  }

  /**
   * 保存引用类槽位选择。
   *
   * @param slot 当前槽位。
   * @param object 当前对象。
   * @returns 写入成功时返回 `true`。
   */
  private storeReferenceSelection(
    slot: LinearComponentPatternSelectionSlot,
    object: THREE.Object3D,
  ): boolean {
    const displayName = this.resolveObjectDisplayName(object);
    const referenceDescriptor = this.createReferenceDescriptor(object);

    if (slot === LinearComponentPatternSelectionSlot.Direction1Reference) {
      this.direction1ReferenceObject = object;
      this.state = {
        ...this.state,
        direction1ReferenceObjectId: object.uuid,
        direction1ReferenceDisplayName: displayName,
        direction1ReferenceDescriptor: referenceDescriptor,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis) {

      // 旋转轴与方向 1 传播方向分开保存，避免 UI 聚焦旋转轴时覆盖阵列方向。
      this.direction1RotationAxisObject = object;
      this.state = {
        ...this.state,
        direction1RotationAxisObjectId: object.uuid,
        direction1RotationAxisDisplayName: displayName,
        direction1RotationAxisDescriptor: referenceDescriptor,
      };
      return true;

    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry) {
      this.direction1ReferenceGeometryObject = object;
      this.state = {
        ...this.state,
        direction1ReferenceGeometryObjectId: object.uuid,
        direction1ReferenceGeometryDisplayName: displayName,
        direction1ReferenceGeometryDescriptor: referenceDescriptor,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1SelectedReference) {
      this.direction1SelectedReferenceObject = object;
      this.state = {
        ...this.state,
        direction1SelectedReferenceObjectId: object.uuid,
        direction1SelectedReferenceDisplayName: displayName,
        direction1SelectedReferenceDescriptor: referenceDescriptor,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2Reference) {
      this.direction2ReferenceObject = object;
      this.state = {
        ...this.state,
        direction2ReferenceObjectId: object.uuid,
        direction2ReferenceDisplayName: displayName,
        direction2ReferenceDescriptor: referenceDescriptor,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry) {
      this.direction2ReferenceGeometryObject = object;
      this.state = {
        ...this.state,
        direction2ReferenceGeometryObjectId: object.uuid,
        direction2ReferenceGeometryDisplayName: displayName,
        direction2ReferenceGeometryDescriptor: referenceDescriptor,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2SelectedReference) {
      this.direction2SelectedReferenceObject = object;
      this.state = {
        ...this.state,
        direction2SelectedReferenceObjectId: object.uuid,
        direction2SelectedReferenceDisplayName: displayName,
        direction2SelectedReferenceDescriptor: referenceDescriptor,
      };
      return true;
    }

    return false;
  }

  /**
   * 解析对象显示名称。
   *
   * @param object 场景对象。
   * @returns 可展示在 PropertyManager 输入框中的名称。
   */
  private resolveObjectDisplayName(object: THREE.Object3D): string {
    const importedName = ImportedModelObjectMetadataService.resolveHierarchy(object)?.name?.trim() ?? '';
    if (importedName.length > 0) {
      return importedName;
    }
    const objectName = object.name.trim();
    if (objectName.length > 0) {
      return objectName;
    }
    return object.type || object.uuid;
  }

  /**
   * 通知选择变化。
   *
   * @param payload 选择变化载荷。
   */
  private notifySelectionChange(payload: LinearComponentPatternSelectionChangePayload): void {
    this.selectionChangeListeners.forEach((listener) => listener(payload));
  }

  /**
   * 清空引用类槽位选择。
   *
   * @param slot 当前槽位。
   * @returns 清空成功时返回 `true`。
   */
  private clearReferenceSelection(slot: LinearComponentPatternSelectionSlot): boolean {
    if (slot === LinearComponentPatternSelectionSlot.Direction1Reference) {
      this.direction1ReferenceObject = null;
      this.state = {
        ...this.state,
        direction1ReferenceObjectId: null,
        direction1ReferenceDisplayName: '',
        direction1ReferenceDescriptor: null,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis) {

      this.direction1RotationAxisObject = null;
      this.state = {
        ...this.state,
        direction1RotationAxisObjectId: null,
        direction1RotationAxisDisplayName: '',
        direction1RotationAxisDescriptor: null,
      };
      return true;

    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry) {
      this.direction1ReferenceGeometryObject = null;
      this.state = {
        ...this.state,
        direction1ReferenceGeometryObjectId: null,
        direction1ReferenceGeometryDisplayName: '',
        direction1ReferenceGeometryDescriptor: null,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction1SelectedReference) {
      this.direction1SelectedReferenceObject = null;
      this.state = {
        ...this.state,
        direction1SelectedReferenceObjectId: null,
        direction1SelectedReferenceDisplayName: '',
        direction1SelectedReferenceDescriptor: null,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2Reference) {
      this.direction2ReferenceObject = null;
      this.state = {
        ...this.state,
        direction2ReferenceObjectId: null,
        direction2ReferenceDisplayName: '',
        direction2ReferenceDescriptor: null,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry) {
      this.direction2ReferenceGeometryObject = null;
      this.state = {
        ...this.state,
        direction2ReferenceGeometryObjectId: null,
        direction2ReferenceGeometryDisplayName: '',
        direction2ReferenceGeometryDescriptor: null,
      };
      return true;
    }
    if (slot === LinearComponentPatternSelectionSlot.Direction2SelectedReference) {
      this.direction2SelectedReferenceObject = null;
      this.state = {
        ...this.state,
        direction2SelectedReferenceObjectId: null,
        direction2SelectedReferenceDisplayName: '',
        direction2SelectedReferenceDescriptor: null,
      };
      return true;
    }

    return false;
  }
}
